package diana.soleil.design.iraniancanadienbazzar.core.data

import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.model.Business
import diana.soleil.design.iraniancanadienbazzar.core.model.BusinessClaim
import diana.soleil.design.iraniancanadienbazzar.core.model.DailyPlatformCounters
import diana.soleil.design.iraniancanadienbazzar.core.model.BusinessStatus
import diana.soleil.design.iraniancanadienbazzar.core.model.Event
import diana.soleil.design.iraniancanadienbazzar.core.model.LocalizedText
import diana.soleil.design.iraniancanadienbazzar.core.model.ListingChangeRequest
import diana.soleil.design.iraniancanadienbazzar.core.model.PendingContentItem
import diana.soleil.design.iraniancanadienbazzar.core.model.ListingRequest
import diana.soleil.design.iraniancanadienbazzar.core.model.ModerationAction
import diana.soleil.design.iraniancanadienbazzar.core.model.Report
import diana.soleil.design.iraniancanadienbazzar.core.model.UserAccount
import kotlinx.coroutines.flow.Flow

/**
 * Admin-lite operations (approvals + moderation queue) — the minimal surface promised for Phase 2 so
 * submitted/claimed listings and filed reports are actionable (`docs/ROADMAP.md`; full admin suite is
 * Phase 6). Every call here requires the signed-in user to have `users/{uid}.role == 'admin'` —
 * Firestore rules enforce it server-side; the UI additionally hides the entry point for non-admins.
 */
interface AdminRepository {
    /** Streams businesses awaiting review (`status == pending`), oldest intent first. */
    fun observePendingBusinesses(): Flow<DataResult<List<Business>>>

    /**
     * Approved listings whose owner has edited them since approval (BIZ-5, Function-flagged).
     * They stay live; re-approving clears the flag, rejecting unpublishes as usual.
     */
    fun observeEditedBusinesses(): Flow<DataResult<List<Business>>>

    /**
     * Recent listings of ANY status (newest first, capped) so the admin can browse/search the whole
     * directory — not just the pending queue. Admin-only read (rules). Capped to a recent window;
     * client-side search filters what's loaded.
     */
    fun observeAllBusinesses(): Flow<DataResult<List<Business>>>

    /**
     * Server-side name search over the WHOLE directory (any status) for the All-listings browser
     * and the offer composer — the recent window above misses older seeds. Prefix match on `name`
     * (a few case variants), capped. Admin-only read (rules).
     */
    fun searchBusinessesByName(prefix: String, limit: Int = 25): Flow<DataResult<List<Business>>>

    /**
     * Approves or rejects a pending listing (admin-only rules path; aggregates recompute via
     * Functions). [reason] (rejections only) is stored on the doc and folded into the submitter's
     * outcome notification so they know what to fix (audit BIZ-6).
     */
    suspend fun setBusinessStatus(businessId: String, status: BusinessStatus, reason: String? = null): DataResult<Unit>

    /** Streams unresolved moderation reports (`status == open`). */
    fun observeOpenReports(): Flow<DataResult<List<Report>>>

    /** Marks a report handled. */
    suspend fun resolveReport(reportId: String): DataResult<Unit>

    /** Streams open public "remove / correct my listing" requests (`listingRequests`, status == open). */
    fun observeListingRequests(): Flow<DataResult<List<ListingRequest>>>

    /** Marks a listing request handled. */
    suspend fun resolveListingRequest(requestId: String): DataResult<Unit>

    /** Streams events awaiting review (`status == pending`). */
    fun observePendingEvents(): Flow<DataResult<List<Event>>>

    /** Approves or rejects a pending event. */
    suspend fun setEventStatus(eventId: String, approved: Boolean, reason: String? = null): DataResult<Unit>

    /**
     * Streams pending ownership claims (`claims` where `status == pending`), newest first — each
     * carrying the claimant's verification details (name, role, contact, message) for the reviewer.
     */
    fun observeClaims(): Flow<DataResult<List<BusinessClaim>>>

    /**
     * Approves a claim: maps `businesses/{businessId}.ownerUid = claimantUid` + `claimStatus = claimed`
     * and marks the claim `approved` (stamping the reviewing admin + time).
     */
    suspend fun approveClaim(claim: BusinessClaim): DataResult<Unit>

    /**
     * Rejects a claim: marks it `rejected` and returns the business to `unclaimed` so it can be
     * re-claimed.
     */
    suspend fun rejectClaim(claim: BusinessClaim): DataResult<Unit>

    /** Streams owners' pending name/category/address change requests (`listingChangeRequests`). */
    fun observePendingListingChanges(): Flow<DataResult<List<ListingChangeRequest>>>

    /**
     * Applies an approved change request to the listing in one batch (name also resets the localized
     * spellings, a new address drops the stale map pin and re-derives the metro) and closes the request.
     */
    suspend fun approveListingChange(request: ListingChangeRequest): DataResult<Unit>

    /** Rejects a change request; [reason] is shown to the owner. The listing is untouched. */
    suspend fun rejectListingChange(request: ListingChangeRequest, reason: String?): DataResult<Unit>

    /**
     * Hidden news, jokes, recipes and facts (`visible == false`) awaiting publication — what the daily
     * AI content job and manual imports add. Oldest-first within each kind, news first.
     */
    fun observePendingContent(): Flow<DataResult<List<PendingContentItem>>>

    /** Makes [item] visible to users (`visible = true`; gems also `status = visible`). */
    suspend fun publishContent(item: PendingContentItem): DataResult<Unit>

    /** Deletes [item] — the reviewer decided it should never be published. */
    suspend fun discardContent(item: PendingContentItem): DataResult<Unit>

    // Moderation actions on report targets (rules: admin OR moderator paths).
    suspend fun hideReview(reviewId: String): DataResult<Unit>
    suspend fun removePost(postId: String): DataResult<Unit>
    suspend fun removeMarketplaceItem(itemId: String): DataResult<Unit>

    // Restore (un-hide) previously removed content — reviews go back to 'visible', marketplace items
    // back to 'active'. (Posts are hard-deleted on removal, so they have no restore path.)
    suspend fun restoreReview(reviewId: String): DataResult<Unit>
    suspend fun restoreMarketplaceItem(itemId: String): DataResult<Unit>

    /**
     * Appends an entry to the moderation audit trail (`moderationActions`), stamped with the acting
     * moderator's uid server-side-verifiably (rules force `actorUid == uid()`). Best-effort: the
     * caller performs the real action first, then logs — a logging failure never blocks moderation.
     */
    suspend fun logModerationAction(
        action: String,
        targetType: String,
        targetId: String,
        reason: String? = null,
    ): DataResult<Unit>

    /** Streams the moderation audit trail newest-first (readable by admins + moderators). */
    fun observeModerationLog(): Flow<DataResult<List<ModerationAction>>>

    /**
     * Streams the signed-in account's own `role` (`user`/`moderator`/`admin`/null) so the console can
     * scope itself: moderators see only the reports queue + audit log (the admin-only queues would
     * permission-deny for them), admins see everything.
     */
    fun observeMyRole(): Flow<String?>

    /**
     * Streams every member account (`users/{uid}` docs — the rules grant list reads to admins only).
     * Fine at community scale; add paging/search before the user base outgrows one query.
     */
    fun observeUsers(): Flow<DataResult<List<UserAccount>>>

    /** Sets a member's role (`users/{uid}.role` — `user`/`moderator`/`admin`), the field rules gate on. */
    suspend fun setUserRole(uid: String, role: String): DataResult<Unit>

    /** The signed-in admin's own uid — used to keep self-demotion out of the UI. */
    val currentUid: String?

    /**
     * Streams the Functions-maintained daily growth buckets (`platform_metrics/daily-*`,
     * admin-read-only), oldest first. Small collection — one doc per active day.
     */
    fun observeGrowthDaily(): Flow<DataResult<List<DailyPlatformCounters>>>

    /**
     * Sends a targeted campaign via the admin-gated `sendCampaign` callable: [channel] picks
     * delivery — push (in-app notification + FCM) or email (SendGrid; server-side the audience is
     * additionally CASL-gated to expressly-consented, non-suppressed users). [metroId] scopes the
     * audience (null = all Canada), [title]/[body] localize per recipient (EN required, FR/FA
     * optional; title doubles as the email subject), [link] is a full app universal-link URL
     * (`https://mahaleh.ca/{b|item|post|event}/{id}`). [campaignId] is minted once per compose
     * session by the caller and reused on retry — the server keys notification/mail docs on it, so
     * a retry after a timeout can never double-send the audience. Returns the recipient count and
     * whether the server truncated the audience at its recipient cap.
     */
    suspend fun sendCampaign(
        campaignId: String,
        metroId: String?,
        title: LocalizedText,
        body: LocalizedText,
        link: String,
        channel: CampaignChannel = CampaignChannel.Push,
    ): DataResult<CampaignSendResult>
}

/** Outcome of [AdminRepository.sendCampaign]: how many recipients, and whether the cap truncated. */
data class CampaignSendResult(val sent: Int, val capped: Boolean)

/** Delivery channel for [AdminRepository.sendCampaign]; wire values match the callable contract. */
enum class CampaignChannel(val wire: String) {
    Push("push"),
    Email("email"),
}
